import { FormEvent, useState } from 'react';
import { useAgentChat } from '@cloudflare/ai-chat/react';
import { useAgent } from 'agents/react';

type SitebotSettings = {
  restUrl: string;
  nonce: string;
  agentHost: string;
};

type AppProps = {
  settings?: SitebotSettings;
};

type ChatPanelProps = {
  settings?: SitebotSettings;
  onClose: () => void;
};

function createChatId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `chat-${crypto.randomUUID()}`;
  }

  return `chat-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function App({ settings }: AppProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div
      className="wp-sitebot"
      data-rest-ready={settings?.restUrl ? 'true' : 'false'}
    >
      {isOpen ? (
        <ChatPanel settings={settings} onClose={() => setIsOpen(false)} />
      ) : null}

      <button
        className="wp-sitebot__fab"
        type="button"
        aria-label={isOpen ? 'Zamknij WP Sitebot' : 'Otworz WP Sitebot'}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <span aria-hidden="true">{isOpen ? 'x' : '?'}</span>
      </button>
    </div>
  );
}

function ChatPanel({ settings, onClose }: ChatPanelProps) {
  const [draft, setDraft] = useState('');
  const [hasConnectionError, setHasConnectionError] = useState(false);
  const [chatId] = useState(createChatId);

  const agent = useAgent({
    agent: 'ChatAgent',
    name: chatId,
    host: settings?.agentHost ?? 'localhost:8000',
    onOpen: () => setHasConnectionError(false),
    onError: () => setHasConnectionError(true),
    onClose: () => setHasConnectionError(true)
  });

  const { messages, sendMessage, status, isStreaming } = useAgentChat({
    agent,
    getInitialMessages: null
  });
  const isBusy = status === 'submitted' || isStreaming;
  const isConnecting = !agent.identified && !hasConnectionError;
  const statusText = hasConnectionError ? 'Brak polaczenia' : isBusy ? 'Pisze...' : 'Online';
  const trimmedDraft = draft.trim();

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!trimmedDraft || isBusy || hasConnectionError) {
      return;
    }

    sendMessage({ text: trimmedDraft });
    setDraft('');
  }

  return (
    <section className="wp-sitebot__panel" aria-label="WP Sitebot chat">
      <header className="wp-sitebot__header">
        <div>
          <strong>WP Sitebot</strong>
          <span>{isConnecting ? 'Laczenie...' : statusText}</span>
        </div>
        <button
          className="wp-sitebot__icon-button"
          type="button"
          aria-label="Zamknij czat"
          onClick={onClose}
        >
          x
        </button>
      </header>

      <div className="wp-sitebot__messages" aria-live="polite">
        {messages.length === 0 ? (
          <p className="wp-sitebot__empty">Czesc! Jestem WP Sitebot. Jak moge pomoc?</p>
        ) : null}

        {messages.map((message) => (
          <div
            className={`wp-sitebot__message wp-sitebot__message--${
              message.role === 'user' ? 'user' : 'assistant'
            }`}
            key={message.id}
          >
            {message.parts.map((part, index) =>
              part.type === 'text' ? <span key={`${message.id}-${index}`}>{part.text}</span> : null
            )}
          </div>
        ))}

        {isBusy ? <div className="wp-sitebot__status">Pisze...</div> : null}
        {hasConnectionError ? (
          <div className="wp-sitebot__status wp-sitebot__status--error">
            Nie moge polaczyc sie z agentem.
          </div>
        ) : null}
      </div>

      <form className="wp-sitebot__form" onSubmit={handleSubmit}>
        <label className="wp-sitebot__label" htmlFor="wp-sitebot-message">
          Wiadomosc
        </label>
        <input
          id="wp-sitebot-message"
          className="wp-sitebot__input"
          type="text"
          value={draft}
          placeholder="Napisz wiadomosc..."
          disabled={hasConnectionError}
          onChange={(event) => setDraft(event.target.value)}
        />
        <button
          className="wp-sitebot__send"
          type="submit"
          aria-label="Wyslij wiadomosc"
          disabled={!trimmedDraft || isBusy || hasConnectionError}
        >
          &gt;
        </button>
      </form>
    </section>
  );
}
